/** Campo "Página X de Y" (US-02).
 *
 * Estado local enquanto o aluno digita, e salto só no `Enter` ou ao sair do
 * campo: saltar a cada tecla faria quem digita "12" passar pela página 1 antes
 * de chegar à 12 — com um render de página inteira do pdf.js no caminho.
 *
 * ⚠️ O critério "Pág. impressa" da US-02 **não se aplica ao PDF**: ele existe
 * para o HTML5, onde a página impressa é marcada por
 * `<span class="print-page" data-page="N">` porque o digital pagina diferente
 * do papel. No PDF a página física É a página, então um segundo campo seria
 * dois controles para a mesma coisa.
 */
import { useState } from 'react'

import { paginaDigitada } from '@/features/reader/utils/swipe'

interface PageInputProps {
  page: number
  total: number
  onIr: (pagina: number) => void
}

export function PageInput({ page, total, onIr }: PageInputProps) {
  const [texto, setTexto] = useState(String(page))

  // O campo segue a navegação feita por fora (sumário, setas, swipe): sem isto
  // ele congelaria no último número digitado e passaria a mentir.
  //
  // Ajuste DURANTE o render, com guarda de valor anterior — o idioma do React
  // para "estado derivado de prop". Por efeito, o campo mostraria o número
  // velho por um quadro a cada virada de página, que é o mesmo defeito que a
  // retomada de posição tinha na `ReaderView`.
  const [paginaVista, setPaginaVista] = useState(page)
  if (paginaVista !== page) {
    setPaginaVista(page)
    setTexto(String(page))
  }

  const confirmar = () => {
    const alvo = paginaDigitada(texto, total)
    if (alvo === null) {
      setTexto(String(page)) // entrada inválida volta ao que vale de verdade
      return
    }
    setTexto(String(alvo))
    if (alvo !== page) onIr(alvo)
  }

  return (
    <div className="page-input">
      <label htmlFor="page-input__campo">Página</label>
      <input
        id="page-input__campo"
        className="page-input__campo"
        // `inputMode` e não `type="number"`: o spinner do number é alvo de
        // toque minúsculo e o campo aceita colar texto qualquer. Assim o
        // celular abre o teclado numérico e a validação fica num lugar só.
        type="text"
        inputMode="numeric"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={confirmar}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            confirmar()
          }
        }}
        aria-label={`Página atual, de ${total || '?'}`}
      />
      <span className="page-input__total">{total > 0 ? `de ${total}` : ''}</span>
    </div>
  )
}
